import { Suspense, useEffect, useRef, useState } from "react";
import { lazyWithRetry } from "../../../lib/lazyWithRetry.js";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

import { api } from "../../../lib/api.js";
import { logError } from "../../../lib/logger.js";
import { GlowCard } from "../../ui/glow-card.js";
import { CardTitle } from "../../ui/card.js";
import { DropdownSelect } from "../../DropdownButton.js";
import { StatusFilterChips } from "../../../pages/employees/StatusFilterChips.js";
import { PRESET_OPTIONS, rangeAndGrain, type Preset } from "../../../pages/expenses/expensePeriod.js";
import type { Analytics } from "../../../pages/expenses/ExpenseAnalyticsCharts.js";
import type { WidgetProps } from "../widget-registry.js";
import { ExpenseReportSkeleton } from "./widget-skeletons.js";

/**
 * The approval flow, in its own order rather than the data's: a fixed list keeps the
 * chips from reordering themselves as the period changes, and shows a status at ₹0
 * instead of dropping it. DRAFT is absent because analytics never counts drafts.
 */
const EXPENSE_STATUSES = [
  { id: "SUBMITTED",        label: "Pending manager approval", swatchClass: "bg-[var(--color-info-fg)]" },
  { id: "MANAGER_APPROVED", label: "Pending finance approval", swatchClass: "bg-[var(--color-accent-fg)]" },
  { id: "FINANCE_APPROVED", label: "Finance approved", swatchClass: "bg-[var(--color-success-fg)]" },
  { id: "PAID",             label: "Paid",             swatchClass: "bg-[var(--color-brand-600)]" },
  { id: "REJECTED",         label: "Rejected",         swatchClass: "bg-[var(--color-error-fg)]" },
] as const;

// Recharts is ~600 kB. The Report tab already splits these charts out for the same
// reason; importing them the same way here keeps them out of the entry chunk and
// shares the one chunk between the two screens.
const ExpenseAnalyticsCharts = lazyWithRetry(() => import("../../../pages/expenses/ExpenseAnalyticsCharts.js"));

/**
 * The Expenses Report tab as a dashboard widget: the five KPI tiles, the spend
 * trend, the category, status and billable donuts and the period table, drawn by
 * the same component the Report tab uses, so the animations and the hover
 * behaviour are the originals rather than a second implementation.
 *
 * It keeps only the period control. The custom range, the currency switch and the
 * Excel download stay on the Report tab, which the heading links to — a dashboard
 * card is a glance, not the full workbench.
 */
export function ExpenseReportWidget(_props: WidgetProps) {
  const [mode, setMode] = useState<Preset>("month");
  const [status, setStatus] = useState("");
  const [currency, setCurrency] = useState("INR");
  const [data, setData] = useState<Analytics | null>(null);
  const [open, setOpen] = useState(false);
  // The period dropdown can be changed faster than a response arrives, so answers
  // are matched to the request that asked for them rather than to arrival order.
  const reqId = useRef(0);

  const range = rangeAndGrain(mode);

  useEffect(() => {
    const id = ++reqId.current;
    const params = new URLSearchParams({
      granularity: range.granularity, from: range.from, to: range.to, currency,
    });
    if (status) params.set("status", status);
    api.get<Analytics>(`/expenses/analytics?${params}`)
      .then((res) => {
        if (id !== reqId.current) return;
        setData(res.data);
        // The viewer may have no spend in the chosen currency at all; follow the
        // Report tab and fall back to the first one the API does have.
        if (!res.data.availableCurrencies.includes(currency) && res.data.availableCurrencies[0]) {
          setCurrency(res.data.availableCurrencies[0]);
        }
      })
      .catch((e: unknown) => { if (id === reqId.current) logError("ExpenseReportWidget.load", e); });
  }, [range.from, range.to, range.granularity, currency, status]);

  const hasData = !!data && data.kpis.itemCount > 0;

  // byStatus is a facet: the API builds it before applying the status filter, so the
  // chips keep their own totals when one of them is selected. The All chip sums them,
  // which is the period's whole spend whatever is selected.
  const money = (n: number) => new Intl.NumberFormat(currency === "INR" ? "en-IN" : "en-US", {
    style: "currency", currency, notation: "compact", maximumFractionDigits: 1,
  }).format(n);
  const totalByStatus = new Map((data?.byStatus ?? []).map((s) => [s.status, s.total]));
  const statusChips = [
    { value: "", label: "All", count: money([...totalByStatus.values()].reduce((sum, t) => sum + t, 0)), swatchClass: "" },
    ...EXPENSE_STATUSES.map((s) => ({
      value: s.id, label: s.label, count: money(totalByStatus.get(s.id) ?? 0), swatchClass: s.swatchClass,
    })),
  ];

  return (
    // @container, because the chart panels have to fold by the card's own width:
    // at Large on a wide screen a viewport breakpoint would still claim two
    // columns inside a two-thirds-width card.
    // spotlight="off": these cards are dense with their own tinted tiles and chart
    // fills, and the interior hover pool washed over them. The edge glow stays.
    <GlowCard glow="ultra" spotlight="off" size="lg" className="@container h-full">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
        <CardTitle>Expense Report</CardTitle>
        <div className="flex items-center gap-3">
          <DropdownSelect
            value={mode}
            onChange={(v) => setMode(v as Preset)}
            options={PRESET_OPTIONS}
            placeholder="Month"
            ariaLabel="Period"
            clearable={false}
            className="w-32 justify-between"
            open={open}
            onToggle={() => setOpen((o) => !o)}
            onClose={() => setOpen(false)}
          />
          <Link
            to="/expenses"
            className="flex items-center gap-1 text-[12px] font-semibold text-brand-600 no-underline hover:underline"
          >
            Full report <ArrowUpRight className="size-3.5" />
          </Link>
        </div>
      </div>

      {/* Until the first response lands there is nothing to draw, so the skeleton
          holds the layout and the charts then animate up from zero into it. Later
          period changes keep the current charts on screen rather than flashing
          back to the skeleton. */}
      {!data ? (
        <ExpenseReportSkeleton />
      ) : (<>
        {/* The chips replace the KPI tiles: each one carries the spend sitting at that
            point in the approval flow, and selecting it narrows every chart below. */}
        <StatusFilterChips
          options={statusChips}
          value={status}
          onChange={setStatus}
          className="mb-4"
          onReset={() => setStatus("")}
          resetDisabled={!status}
        />
        {!hasData ? (
        <p className="py-10 text-center text-[13px] text-surface-400">
          No submitted expense data for this period.
        </p>
      ) : (
        <Suspense fallback={<ExpenseReportSkeleton />}>
          <ExpenseAnalyticsCharts
            data={data}
            currency={currency}
            granularity={range.granularity}
            // Two donuts side by side only once the card is wide enough for them;
            // below that they stack rather than squeezing out their legends.
            pairGridClassName="@3xl:grid-cols-2"
            splitGridClassName="@3xl:grid-cols-[1fr_1.4fr]"
            // The chips above carry the same totals the tiles did, and five of them
            // wrapped onto two rows inside a card.
            showKpis={false}
          />
        </Suspense>
      )}
      </>)}
    </GlowCard>
  );
}
